import { type ReactNode, type HTMLAttributes } from 'react';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';

interface GlassCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Card title — renders the header when provided. String or ReactNode. */
  title?: ReactNode;
  /** Icon to the RIGHT of the title (ReactNode, e.g. lucide icon) */
  icon?: ReactNode;
  /** Action element on the opposite side of the header (link, button) */
  headerAction?: ReactNode;
  /** Content padding: 'default' (p-5), 'compact' (p-3 md:p-4), 'none' (no padding) */
  padding?: 'default' | 'compact' | 'none';
  /** Extra classes for the body/content wrapper */
  contentClassName?: string;
  /** Override classes for the header row (replaces default px-5 py-2.5) */
  headerClassName?: string;
  /** Override inline styles for the header row */
  headerStyle?: React.CSSProperties;
}

const paddingMap = {
  default: 'p-5',
  compact: 'p-3 md:p-4',
  none: '',
} as const;

export function GlassCard({
  title,
  icon,
  headerAction,
  padding = 'default',
  contentClassName,
  headerClassName,
  headerStyle: headerStyleProp,
  className,
  children,
  ...rest
}: GlassCardProps) {
  const rightElement = headerAction ?? (icon ? (
    <span style={{ color: ADMIN_COLORS.textTertiary }}>{icon}</span>
  ) : null);

  return (
    <div
      className={`rounded-3xl overflow-hidden border border-white/30 glass-surface ${className ?? ''}`}
      {...rest}
    >
      {title && (
        <div
          className={`${headerClassName ?? 'px-5 py-2.5'} flex items-center justify-between border-b border-white/30`}
          style={headerStyleProp ?? { backgroundColor: 'var(--page-card-bg, rgba(255, 255, 255, 0.7))' }}
        >
          <h2 className="font-semibold text-xs sm:text-sm" style={{ color: ADMIN_COLORS.navy }}>
            {title}
          </h2>
          {rightElement}
        </div>
      )}
      <div className={`${paddingMap[padding]} ${contentClassName ?? ''}`}>
        {children}
      </div>
    </div>
  );
}
